"use client";

import MediaImage from "@/components/ui/MediaImage";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useState } from "react";
import { preload } from "react-dom";
import clsx from "clsx";
import CaseDetailLabels from "./CaseDetailLabels";
import { newCaseLabelStyle } from "@/utils/case-label-style";
import ZoomImage from "./ZoomImage";
import IconButton from "./IconButton";
import {
  faCalendarCirclePlus,
  faImage,
  faLocationDot,
  faTableLayout,
  faVideo,
  faCube,
  faPlay,
  faUpRightFromSquare,
} from "@fortawesome/sharp-light-svg-icons";
import { CaseData, CMSData, CMSSection } from "@/cms/models";
import { AnimateNumber } from "motion-number";
import { useLookTransition } from "@/components/motion/LookMotion";
import {
  createIcsEvent,
  getCaseDetailSublabel,
  getOpenHouseText,
} from "@/utils/helper";
import RippleCtaButton from "./RippleCtaButton";
import {
  getDesktop,
  getMobile,
  type OwnSectionHeights,
} from "@/utils/theme-helper";
import CaseMediaViewer from "./CaseMediaViewer";
import {
  defaultCaseGalleryViewer,
  getCaseGalleryImages,
  scrollToCaseImageGrid,
  type CaseGalleryMedia,
  type CaseGallerySettings,
  type CaseGalleryViewerSetting,
} from "@/utils/case-gallery";

interface ImageGalleryProps {
  caseData: CaseData;
  cmsData: CMSData;
  cmsSection: CMSSection<CaseGallerySettings>;
  viewer: CaseGalleryViewerSetting;
  /** With viewer "none": the grid section the photos scroll to; absent leaves them unclickable. */
  gridAnchor?: string;
  /** The devices on which the section has a Højde for the photos to fill. */
  ownHeights?: OwnSectionHeights;
}

/**
 * On a device the section has a Højde for, the case video at the top fills it, but is never lower than 300 px. The video
 * is then out of flow, since in flow its own shape at the section's width would set the height.
 */
const HERO_VIDEO_HEIGHT: Record<OwnSectionHeights, string> = {
  none: "h-[300px] md:h-[500px]",
  both: "min-h-[304px]",
  mobile: "min-h-[304px] md:h-[500px]",
  desktop: "h-[300px] md:h-auto md:min-h-[304px]",
};

const COUNT_TRANSITION = {
  delay: 0.05,
  duration: 0.2,
  ease: "easeOut",
} as const;

const ImageGallery: React.FC<ImageGalleryProps> = ({
  caseData,
  cmsData,
  cmsSection,
  viewer,
  gridAnchor,
  ownHeights = "none",
}) => {
  const fillsMobile = ownHeights === "both" || ownHeights === "mobile";
  const fillsDesktop = ownHeights === "both" || ownHeights === "desktop";
  const viewerOff = viewer === "none";
  const mediaViewer = viewerOff ? defaultCaseGalleryViewer({}) : viewer;
  const photosInteractive = !viewerOff || !!gridAnchor;
  const openPhoto = (index: number) =>
    viewerOff
      ? gridAnchor
        ? scrollToCaseImageGrid
        : undefined
      : () => handleFullscreen(true, "photo", index);
  const modernLabels = newCaseLabelStyle(cmsData.theme.label);
  const galleryImages = useMemo(
    () => getCaseGalleryImages(caseData),
    [caseData],
  );
  // The case details query asks for images without an imageType, so galleryImages
  // also holds drawings, maps and qrcodes. Those have their own menu (Plantegning)
  // or no viewer at all, so the tiles and the counter must only see photos.
  const heroImages = useMemo(
    () => galleryImages.filter((x) => x.namespace.endsWith("photo")),
    [galleryImages],
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [filter, setFilter] = useState<CaseGalleryMedia>("photo");
  const [initialIndex, setInitialIndex] = useState(0);
  const countTransition = useLookTransition(COUNT_TRANSITION);
  const nextOpenhouse = caseData.openhouses[0];
  const openHouseText = nextOpenhouse
    ? nextOpenhouse.bookingRequired
      ? `Åbent hus med tilmelding - ${getOpenHouseText(nextOpenhouse)}`
      : `Åbent hus - ${getOpenHouseText(nextOpenhouse)}`
    : undefined;

  // Antal billeder is saved as a number when mobile and computer show the same count.
  const numberOfImages = String(cmsSection.specifics?.numberOfImages ?? "");
  const numberOfImagesMobileString = getMobile("3", numberOfImages || "3");
  const numberOfImagesMobile = parseInt(numberOfImagesMobileString, 10);
  const numberOfImagesDesktopString = getDesktop("4", numberOfImages || "4");
  const numberOfImagesDesktop = parseInt(numberOfImagesDesktopString, 10);
  const isSecondaryLayout = cmsData.layout?.name === "secondary";
  const isClausBorg = cmsData.subdomain === "claus-borg";
  const renderGalleryAction = ({
    icon,
    onClick,
    ariaLabel,
    label,
    isActive = false,
  }: {
    icon: IconDefinition;
    onClick: () => void;
    ariaLabel: string;
    label: string;
    isActive?: boolean;
  }) => {
    if (!isClausBorg) {
      return (
        <IconButton
          suppressRoundedClass={true}
          icon={icon}
          onClick={onClick}
          ariaLabel={ariaLabel}
          label={label}
        />
      );
    }

    const activeColor = "#bf8f24";

    return (
      <RippleCtaButton
        suppressRoundedClass={true}
        aria-label={ariaLabel}
        onClick={onClick}
        config={{
          bgColor: isActive ? activeColor : "#ffffff",
          textColor: isActive ? "#ffffff" : "#173143",
          borderColor: "transparent",
          hoverBgColor: isActive ? activeColor : "#f7f8f9",
          hoverTextColor: isActive ? "#ffffff" : "#173143",
          hoverBorderColor: "transparent",
          padding: "",
        }}
        className="flex h-10 min-w-10 items-center justify-center gap-2 border-0 border-r border-[#edf0f3] px-3 text-xs font-semibold last:border-r-0 md:min-w-[118px] md:px-5"
      >
        <FontAwesomeIcon className="size-4" icon={icon} />
        <span className="hidden md:inline">{label}</span>
      </RippleCtaButton>
    );
  };

  const handleFullscreen = (
    open: boolean,
    filter: CaseGalleryMedia = "photo",
    imageIndex = 0,
  ) => {
    setFilter(filter);
    setInitialIndex(imageIndex);
    setIsFullscreen(open);
  };

  const galleryActions = galleryImages.length > 0 && (
    <div
      className={
        isClausBorg
          ? "flex h-10 overflow-hidden rounded-[5px] bg-white shadow-[0_1px_7px_rgba(0,0,0,0.12)]"
          : "h-14 flex shadow-lg homepage-button-rounded overflow-auto"
      }
    >
      {photosInteractive &&
        renderGalleryAction({
          icon: faImage,
          onClick: viewerOff
            ? scrollToCaseImageGrid
            : () => handleFullscreen(true),
          ariaLabel: "Billeder",
          label: "Billeder",
          isActive: true,
        })}
      {galleryImages.some((x) => x.namespace.endsWith("drawing")) && (
        <>
          {renderGalleryAction({
            icon: faTableLayout,
            onClick: () => {
              handleFullscreen(true, "drawing");
            },
            ariaLabel: "Plantegning",
            label: "Plantegning",
          })}
        </>
      )}
      {caseData.videoUrls?.length > 0 && (
        <>
          {renderGalleryAction({
            icon: faVideo,
            onClick: () => {
              handleFullscreen(true, "video");
            },
            ariaLabel: "Video",
            label: "Video",
          })}
        </>
      )}
      {renderGalleryAction({
        icon: faLocationDot,
        onClick: () => {
          document
            .getElementById("caseMapSection")
            ?.scrollIntoView({ behavior: "smooth" });
        },
        ariaLabel: "Kort",
        label: "Kort",
      })}
      {caseData.videoLinks?.map((link) => (
        <React.Fragment key={link.url}>
          {renderGalleryAction({
            icon:
              cmsSection.specifics?.videolinkIconName === "cube"
                ? faCube
                : cmsSection.specifics?.videolinkIconName === "play"
                  ? faPlay
                  : faUpRightFromSquare,
            onClick: () => {
              window.open(link.url, "_blank");
            },
            ariaLabel: link.displayName,
            label:
              link.displayName.substring(0, 12) +
              (link.displayName.length > 12 ? "..." : ""),
          })}
        </React.Fragment>
      ))}
    </div>
  );

  const labelOverlay = modernLabels ? (
    <CaseDetailLabels caseData={caseData} settings={cmsData.theme.label} />
  ) : (
    (caseData.marketingTexts?.label ||
      getCaseDetailSublabel(caseData) ||
      (caseData.isActive && caseData.openhouses.length > 0)) && (
      <div
        className={`hidden homepage-text-sm md:flex gap-2 flex-col absolute top-0 inset-x-0 bg-opacity-80 ${
          cmsData.theme.appearance || cmsData.theme.presentation === "classic"
            ? "bg-[var(--gallery-bg-color,white)]"
            : "bg-white"
        } text-[var(--gallery-text-color,inherit)] p-2`}
      >
        {caseData.marketingTexts?.label && (
          <span className="homepage-text-lg uppercase">
            {caseData.marketingTexts?.label}
          </span>
        )}
        {getCaseDetailSublabel(caseData) && (
          <span className="homepage-text-lg uppercase">
            {getCaseDetailSublabel(caseData)}
          </span>
        )}
        {caseData.isActive && caseData.openhouses.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span>{openHouseText}</span>
              <button
                aria-label="download calendar appointment"
                onClick={() => {
                  const openhouse = caseData.openhouses[0];
                  const icsContent = createIcsEvent(
                    openHouseText!,
                    new Date(openhouse.startDate!),
                    new Date(openhouse.endDate!),
                    openhouse.location,
                  );
                  const blob = new Blob([icsContent], {
                    type: "text/calendar",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `${caseData.advertisementAddressOrDisplayName}.ics`;
                  link.click();
                  URL.revokeObjectURL(url);
                }}
              >
                <FontAwesomeIcon
                  className="size-5"
                  icon={faCalendarCirclePlus}
                />
              </button>
            </div>
            <div>
              {caseData.openhouses.find((x) => x.bookingRequired) && (
                <RippleCtaButton
                  onClick={(e) => {
                    if (!window) {
                      return;
                    }

                    window.dispatchEvent(
                      new CustomEvent("selectedTabEvent", {
                        detail: { selectedTab: "openhouse" },
                      }),
                    );
                    e.preventDefault();
                    document
                      .getElementById("caseContactSection")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Tilmeld
                </RippleCtaButton>
              )}
            </div>
          </div>
        )}
      </div>
    )
  );

  const [heroVideoFailed, setHeroVideoFailed] = useState(false);
  const heroVideoUrl =
    cmsData.caseHeroVideo && !heroVideoFailed
      ? caseData.heroVideoUrl
      : undefined;
  const heroPosterUrl = heroVideoUrl ? heroImages[0]?.lgUrl : undefined;
  if (heroPosterUrl) {
    preload(heroPosterUrl, { as: "image", fetchPriority: "high" });
  }
  const [heroVideoStarted, setHeroVideoStarted] = useState(false);
  const [heroVideoPlaying, setHeroVideoPlaying] = useState(false);
  // The src is set after load so the poster, not the video, determines LCP.
  useEffect(() => {
    const saveData = (navigator as { connection?: { saveData?: boolean } })
      .connection?.saveData;
    if (!heroVideoUrl || saveData) {
      return;
    }
    const startVideo = () => setHeroVideoStarted(true);
    if (document.readyState === "complete") {
      startVideo();
      return;
    }
    window.addEventListener("load", startVideo, { once: true });
    return () => window.removeEventListener("load", startVideo);
  }, [heroVideoUrl]);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-1 relative">
        {isFullscreen && (
          <CaseMediaViewer
            caseData={caseData}
            variant={mediaViewer}
            view={filter}
            initialIndex={initialIndex}
            onClose={() => handleFullscreen(false)}
          />
        )}
        {heroVideoUrl ? (
          <div
            className={`w-full relative overflow-hidden pt-1 ${HERO_VIDEO_HEIGHT[ownHeights]}`}
          >
            <div
              className={
                ownHeights === "none"
                  ? "relative h-full w-full"
                  : "absolute inset-x-0 bottom-0 top-1"
              }
            >
              {/* Bottom: the video. Its poster keeps LCP on the photo; without it the first frame becomes a late LCP candidate. */}
              <video
                autoPlay
                muted
                loop
                playsInline
                poster={heroPosterUrl}
                src={heroVideoStarted ? heroVideoUrl : undefined}
                onPlaying={() => setHeroVideoPlaying(true)}
                onError={() => setHeroVideoFailed(true)}
                className="h-full w-full object-cover"
              />
              {/* Top: the same photo, shown until the video plays, then faded out. */}
              {heroPosterUrl && (
                <MediaImage
                  src={heroPosterUrl}
                  alt=""
                  className={clsx(
                    "pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-500",
                    heroVideoPlaying ? "opacity-0" : "opacity-100",
                  )}
                />
              )}
            </div>
            {labelOverlay}
          </div>
        ) : heroImages[0] ? (
          <div className="w-full flex flex-col md:flex-row flex-1 gap-1 pt-1">
            <div
              className={`hidden md:flex overflow-hidden flex-1 relative ${fillsDesktop ? "min-h-[300px]" : "h-[500px]"}`}
            >
              <ZoomImage
                onClick={openPhoto(0)}
                interactive={photosInteractive}
                src={heroImages[0]?.lgUrl}
                alt="image 01"
              />
              {labelOverlay}
            </div>
            <div
              className={`flex md:hidden overflow-hidden relative ${fillsMobile ? "grow min-h-[300px]" : "h-[300px]"}`}
            >
              <ZoomImage
                onClick={openPhoto(0)}
                interactive={photosInteractive}
                src={heroImages[0]?.lgUrl}
                alt="image 01"
              />
              {modernLabels && labelOverlay}
            </div>
            {numberOfImagesMobile > 1 && heroImages[1] && (
              <div
                className={`flex md:hidden gap-1 ${numberOfImagesMobile > 2 ? "h-[180px]" : "h-[300px]"}`}
              >
                <div className="flex overflow-hidden flex-1 relative">
                  <ZoomImage
                    onClick={openPhoto(1)}
                    interactive={photosInteractive}
                    src={heroImages[1]?.lgUrl}
                    alt="image 02"
                  />
                </div>

                {numberOfImagesMobile > 2 && heroImages[2] && (
                  <div className="flex overflow-hidden flex-1 relative">
                    <ZoomImage
                      onClick={openPhoto(2)}
                      interactive={photosInteractive}
                      src={heroImages[2]?.lgUrl}
                      alt="image 03"
                    />
                  </div>
                )}
              </div>
            )}
            {numberOfImagesDesktop > 1 && heroImages[1] && (
              <div className="hidden md:flex flex-1 flex-col gap-1">
                <div className="flex overflow-hidden flex-1 relative">
                  <ZoomImage
                    onClick={openPhoto(1)}
                    interactive={photosInteractive}
                    src={heroImages[1]?.lgUrl}
                    alt="image 02"
                  />
                </div>

                {numberOfImagesDesktop > 2 && heroImages[2] && (
                  <div className="flex flex-1 gap-1">
                    <div className="flex overflow-hidden flex-1 relative">
                      <ZoomImage
                        onClick={openPhoto(2)}
                        interactive={photosInteractive}
                        src={heroImages[2]?.lgUrl}
                        alt="image 03"
                      />
                    </div>

                    {numberOfImagesDesktop > 3 && heroImages[3] && (
                      <div className="flex flex-1 relative overflow-hidden">
                        <ZoomImage
                          onClick={openPhoto(3)}
                          interactive={photosInteractive}
                          src={heroImages[3]?.lgUrl}
                          alt="image 04"
                        >
                          <div className="absolute inset-0 bg-gray-900 bg-opacity-50">
                            <div className="flex flex-1 h-full gap-2 homepage-text-sm text-white items-center justify-center">
                              <FontAwesomeIcon
                                className="size-6"
                                icon={faImage}
                              />
                              <AnimateNumber
                                format={{
                                  style: "decimal",
                                }}
                                transition={countTransition}
                                style={{ originX: 0.5, originY: 1.5 }}
                                className="text-xl"
                              >
                                {heroImages.length}
                              </AnimateNumber>
                            </div>
                          </div>
                        </ZoomImage>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`hidden md:flex w-full ${fillsDesktop ? "min-h-[300px]" : "h-[500px]"} bg-[var(--gallery-placeholder-bg-color,#d1d5db)]`}
          ></div>
        )}
        {!isSecondaryLayout && !isClausBorg && galleryActions && (
          <div className="absolute flex -bottom-7 inset-x-0 z-20 justify-center">
            {galleryActions}
          </div>
        )}
      </div>
      {(isSecondaryLayout || isClausBorg) && galleryActions && (
        <div className="mx-auto flex w-full max-w-screen-2xl justify-start pt-10 z-20 homepage-pr-md homepage-pl-md md:homepage-pr-lg md:homepage-pl-lg">
          {galleryActions}
        </div>
      )}
      {!modernLabels && (
        <div className="flex flex-col md:hidden pt-10 pb-4 justify-center items-center">
          {caseData.marketingTexts?.label && (
            <div className="homepage-text-lg px-4 uppercase">
              {caseData.marketingTexts?.label}
            </div>
          )}
          {getCaseDetailSublabel(caseData) && (
            <span className="homepage-text-lg  px-4 uppercase">
              {getCaseDetailSublabel(caseData)}
            </span>
          )}
          {caseData.isActive && caseData.openhouses.length > 0 && (
            <div className="flex homepage-text-sm flex-col gap-2 px-4">
              <div className="flex items-center gap-2">
                <span>{openHouseText}</span>
              </div>
              <div>
                {caseData.openhouses.find((x) => x.bookingRequired) && (
                  <div className="flex gap-2">
                    <RippleCtaButton
                      onClick={() => {
                        const icsContent = createIcsEvent(
                          openHouseText!,
                          new Date(caseData.openhouses[0].startDate!),
                          new Date(caseData.openhouses[0].endDate!),
                        );
                        const blob = new Blob([icsContent], {
                          type: "text/calendar",
                        });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `${caseData.advertisementAddressOrDisplayName}.ics`;
                        link.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <FontAwesomeIcon
                        className="size-5"
                        icon={faCalendarCirclePlus}
                      />
                    </RippleCtaButton>
                    <RippleCtaButton
                      onClick={(e) => {
                        if (!window) {
                          return;
                        }

                        window.dispatchEvent(
                          new CustomEvent("selectedTabEvent", {
                            detail: { selectedTab: "openhouse" },
                          }),
                        );
                        e.preventDefault();
                        document
                          .getElementById("caseContactSection")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      Tilmeld
                    </RippleCtaButton>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {modernLabels && !heroVideoUrl && !heroImages[0] && (
        <CaseDetailLabels
          inline
          caseData={caseData}
          settings={cmsData.theme.label}
        />
      )}
    </div>
  );
};

export default ImageGallery;
